import { HttpClient, HttpResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment.development';
import { GetFarmResponseType, GetAllProductsResponseType, Product, SendCustomerInfo, Review, Farm } from '../../models/types';
import { jwtDecode } from 'jwt-decode';
import { LoginService } from './login.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MessageProductOwnerComponent } from '../components/message-product-owner/message-product-owner.component';
import { ProductDetailsModalComponent } from '../components/product-details-modal/product-details-modal.component';
import { AddEventComponent } from '../components/add-event/add-event.component';

@Injectable({
  providedIn: 'root'
})

export class MarketPageService {
  private modalRef: NgbModalRef | null = null;

  private eventAddedSubject = new Subject<void>(); // Signal for event addition
  eventAdded$ = this.eventAddedSubject.asObservable();

  constructor(
    private http: HttpClient, 
    private loginService: LoginService,
    private modalService: NgbModal) { }

  getAllFarms(): Observable<HttpResponse<GetFarmResponseType>> {
    return this.http.get<GetFarmResponseType>(environment.getAllFarmsURL, {
        observe: 'response'
    });
  }

  deleteUser(userID: number): Observable<any> {
    return this.http.delete(`${environment.deleteUserURL}/${userID}`, {
      headers: {
        ...(this.loginService.IsValidAdminToken() && {authorization: this.loginService.GetToken()!})
      }
    });
  }

  getAllProducts(): Observable<HttpResponse<GetAllProductsResponseType>> {
    return this.http.get<GetAllProductsResponseType>(environment.productURL, {
        observe: 'response'
    });
  }

  sendMessageToProductOwner(product: Product, customerInfo: SendCustomerInfo): Observable<any> {
    return this.http.post(environment.sendMessageToProductOwnerURL, {
      product: product,
      customerInfo: customerInfo
    });
  }

  sendAdminMessage(message: String, farmID: number): Observable<any> {
    return this.http.post(environment.sendAdminMessage, { message: message, farmID: farmID }, {
      headers: {
        ...(this.loginService.IsValidAdminToken() && {authorization: this.loginService.GetToken()!})
      },
      observe: 'response',
    });
  }

  sendReviewToProductOwner(product: Product, review: Review): Observable<any> {
    return this.http.post(`${environment.productURL}/${product.productID}/reviews`, { review });
  }

  subscribeToFarm(farm: Farm, userID: number): Observable<any> {
    return this.http.post(`${environment.farmURL}/${farm.farmID}/subscribe`, { userID }, { observe: 'response' });
  }

  deleteFarm(farmID: number): Observable<any> {
    return this.http.delete(`${environment.farmURL}/delete/${farmID}`, {
      headers: {
        ...(this.loginService.IsValidAdminToken() && {authorization: this.loginService.GetToken()!})
      }
    });
  }

  getAllUsers(): Observable<any> {
    return this.http.get(environment.getAllUsersURL);
  }

  testServer(farm: Farm): Observable<any> {
    return this.http.get(environment.testServerURL);
  }

  sendMessageToFarm(customerInfo: SendCustomerInfo, farmID: number): Observable<any> {
    return this.http.post(environment.sendMessageToFarmURL, { customerInfo, farmID });
  }

  //Modal services
  openContactModal(product: Product) {
    this.modalRef = this.modalService.open(MessageProductOwnerComponent, { centered: true });
    this.modalRef!.componentInstance.product = product;
  }

  closeContactProductModal() {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
    }
  }

  openReviewModal(product: Product) {
    this.modalRef = this.modalService.open(ProductDetailsModalComponent, { centered: true });
    this.modalRef!.componentInstance.product = product;
  }

  openEventAddModal(isOrgAdmin: boolean, organisationID: string) {
    console.log('Opening event add modal in event service');
    this.modalRef = this.modalService.open(AddEventComponent, { centered: true, size: 'xl' });
    this.modalRef.componentInstance.isOrgAdmin = isOrgAdmin
    this.modalRef.componentInstance.organisationID = organisationID

    this.modalRef.componentInstance.eventAdded.subscribe(() => {
      this.eventAddedSubject.next();
    });
  }

}
